import { useState, useEffect, useMemo, useCallback } from "react";
import Input from "../campaign/wizard/Input";
import { INDUSTRIES, getCategoriesForIndustry } from "../../constants/configs";
import { COUNTRIES } from "../../constants/countries";
import { DEFAULT_COUNTRY, pickProfileFormData } from "../../utils/businessProfilePayload";
import {
  MAX_BUSINESS_DESCRIPTION_WORDS,
  MAX_LOCATION_WORDS,
  countWords,
  getBusinessWebsiteError,
  getWordLimitError,
} from "../../utils/textFieldValidation";

export const defaultData = {
  name: "",
  tagline: "",
  description: "",
  industry: "",
  category: "",
  country: DEFAULT_COUNTRY,
  website: "",
  location: "",
  keywords: "",
  uniqueness: "",
};

export function validateBasicDetailsFields(formData = {}) {
  const errors = {};

  if (!String(formData.name ?? "").trim()) {
    errors.name = "Business name is required";
  }

  const descriptionError = getWordLimitError(
    formData.description,
    MAX_BUSINESS_DESCRIPTION_WORDS,
    "Description"
  );
  if (descriptionError) {
    errors.description = descriptionError;
  }

  return errors;
}

export function validateIndustryInfoFields(formData = {}) {
  const errors = {};

  if (!String(formData.industry ?? "").trim()) {
    errors.industry = "Industry is required";
  }
  if (!String(formData.category ?? "").trim()) {
    errors.category = "Category is required";
  }
  if (!String(formData.country ?? "").trim()) {
    errors.country = "Country is required";
  }

  const websiteError = getBusinessWebsiteError(formData.website);
  if (websiteError) {
    errors.website = websiteError;
  }

  const locationError = getWordLimitError(formData.location, MAX_LOCATION_WORDS, "Location");
  if (locationError) {
    errors.location = locationError;
  }

  return errors;
}

export function isIndustryInfoValid(formData = {}) {
  return Object.keys(validateIndustryInfoFields(formData)).length === 0;
}

export function validateEditProfileFields(formData = {}) {
  const errors = validateBasicDetailsFields(formData);

  const websiteError = getBusinessWebsiteError(formData.website);
  if (websiteError) {
    errors.website = websiteError;
  }

  return errors;
}

export function validateProfileFormFields(formData = {}) {
  return {
    ...validateBasicDetailsFields(formData),
    ...validateIndustryInfoFields(formData),
  };
}

const ERROR_BORDER = "border-red-500 focus:border-red-500 focus:ring-red-500/35";

export function LocationCountryField({
  formData,
  handleChange,
  countryOptions,
  isReadOnly = false,
  wrapperClassName = "mb-4 col-12 col-md-6",
  fieldErrors = {},
  compact = false,
}) {
  const countryErrorBorder = fieldErrors.country
    ? "border-red-500 focus:border-red-500 focus:ring-red-500/35"
    : "";

  const labelClass = compact
    ? "form-label mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-dark/90"
    : "form-label mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-dark/90 md:mb-3 md:gap-2.5 md:text-sm md:tracking-[0.15em]";

  const iconWrapClass = compact
    ? "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-neutral-100/75 text-dark/80"
    : "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-neutral-100/75 text-dark/80 md:h-8 md:w-8";

  const inputClass = compact
    ? "w-full min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-[11px] text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 hover:border-slate-500 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/35"
    : "w-full min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2.5 text-xs text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 hover:border-slate-500 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/35 md:px-4 md:py-3 md:text-sm";

  const selectClass = compact
    ? `w-full shrink-0 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-[11px] text-slate-900 outline-none transition-all duration-200 hover:border-slate-500 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/35 ${countryErrorBorder}`
    : `w-full shrink-0 rounded-md border border-slate-300 bg-white px-3 py-2.5 text-xs text-slate-900 outline-none transition-all duration-200 hover:border-slate-500 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/35 md:px-4 md:py-3 md:text-sm ${countryErrorBorder}`;

  const hintClass = compact ? "mt-1 text-[10px] text-slate-500" : "mt-1 text-xs text-slate-500 md:text-sm";
  const errorClass = compact ? "mt-1 text-[10px] text-red-600" : "mt-1 text-xs text-red-600 md:text-sm";

  return (
    <div className={wrapperClassName}>
      <label className={labelClass}>
        <span className={iconWrapClass}>
          <i className={`bi bi-geo-alt ${compact ? "text-[10px]" : "text-sm md:text-base"}`} aria-hidden />
        </span>
        Location
      </label>
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch">
        <input
          type="text"
          name="location"
          value={formData.location}
          onChange={handleChange}
          readOnly={isReadOnly}
          placeholder="City, area, pin code"
          className={inputClass}
        />
        <div className={compact ? "w-full sm:w-36" : "w-full sm:w-40"}>
          <select
            name="country"
            value={formData.country || DEFAULT_COUNTRY}
            onChange={handleChange}
            disabled={isReadOnly}
            required
            className={selectClass}
            aria-label="Country"
            aria-invalid={Boolean(fieldErrors.country)}
          >
            {countryOptions.map((option) => (
              <option
                key={option.value ?? option.label}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          {fieldErrors.country ? (
            <p className={errorClass}>{fieldErrors.country}</p>
          ) : null}
        </div>
      </div>
      {fieldErrors.location ? (
        <p className={errorClass}>{fieldErrors.location}</p>
      ) : (
        <p className={hintClass}>
          {countWords(formData.location)}/{MAX_LOCATION_WORDS} words
        </p>
      )}
    </div>
  );
}

export function useBusinessProfileForm({
  initialData,
  mode = "create",
  readOnly: readOnlyProp,
  inputSize = "md",
} = {}) {
  const [formData, setFormData] = useState(defaultData);
  const isReadOnly = readOnlyProp ?? mode === "view";

  useEffect(() => {
    if (mode === "create" && !initialData) {
      setFormData({ ...defaultData });
      return;
    }
    if (initialData) {
      setFormData(pickProfileFormData(initialData));
    }
  }, [initialData, mode]);

  const handleChange = useCallback(
    (e) => {
      if (isReadOnly) return;
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [isReadOnly]
  );

  const handleIndustryChange = useCallback(
    (e) => {
      if (isReadOnly) return;
      const value = e.target.value;
      setFormData((prev) => {
        const cats = getCategoriesForIndustry(value);
        const keepCategory = value && cats.includes(prev.category);
        return { ...prev, industry: value, category: keepCategory ? prev.category : "" };
      });
    },
    [isReadOnly]
  );

  const industryOptions = useMemo(() => {
    const base = [{ value: "", label: "Select industry" }];
    const list = INDUSTRIES.map((i) => ({ value: i, label: i }));
    if (formData.industry && !INDUSTRIES.includes(formData.industry)) {
      return [...base, { value: formData.industry, label: `${formData.industry}` }, ...list];
    }
    return [...base, ...list];
  }, [formData.industry]);

  const categoryOptions = useMemo(() => {
    const placeholder = formData.industry
      ? { value: "", label: "Select category" }
      : { value: "", label: "Select an industry first" };
    const cats = formData.industry ? getCategoriesForIndustry(formData.industry) : [];
    const opts = [placeholder];
    if (formData.category && formData.industry && !cats.includes(formData.category)) {
      opts.push({
        value: formData.category,
        label: `${formData.category} (update to list)`,
      });
    }
    cats.forEach((c) => opts.push({ value: c, label: c }));
    return opts;
  }, [formData.industry, formData.category]);

  const countryOptions = useMemo(() => {
    const base = [{ value: "", label: "Select a country" }];
    const list = COUNTRIES.map((c) => ({ value: c, label: c }));
    const current = formData.country || DEFAULT_COUNTRY;
    if (current && !COUNTRIES.includes(current)) {
      return [...base, { value: current, label: current }, ...list];
    }
    return [...base, ...list];
  }, [formData.country]);

  const renderField = useCallback(
    (
      label,
      name,
      type = "text",
      rows = 1,
      column = 12,
      labelIcon = null,
      { fieldError = null, required = false, disabled = false, maxWords = null } = {}
    ) => {
      const wordCount = maxWords != null ? countWords(formData[name]) : null;
      const fieldProps = {
        label,
        labelIcon,
        name,
        value: formData[name],
        onChange: handleChange,
        readOnly: isReadOnly || disabled,
        disabled: disabled || undefined,
        required,
        size: inputSize,
        wrapperClassName: fieldError
          ? "mb-0"
          : `${inputSize === "sm" ? "mb-3" : "mb-4"} col-12 col-md-${column}`,
        className: fieldError ? ERROR_BORDER : "",
      };

      const input =
        rows > 1 ? (
          <Input as="textarea" rows={rows} {...fieldProps} />
        ) : (
          <Input type={type || "text"} {...fieldProps} />
        );

      const wordCountHint =
        maxWords != null ? (
          <p
            className={`mt-1 ${inputSize === "sm" ? "text-[10px]" : "text-xs md:text-sm"} ${
              wordCount > maxWords ? "text-red-600" : "text-slate-500"
            }`}
          >
            {wordCount}/{maxWords} words
          </p>
        ) : null;

      if (!fieldError && !wordCountHint) return input;

      return (
        <div className={`${inputSize === "sm" ? "mb-3" : "mb-4"} col-12 col-md-${column}`}>
          {input}
          {fieldError ? (
            <p className={`mt-1 ${inputSize === "sm" ? "text-[10px]" : "text-xs md:text-sm"} text-red-600`}>
              {fieldError}
            </p>
          ) : (
            wordCountHint
          )}
        </div>
      );
    },
    [formData, handleChange, isReadOnly, inputSize]
  );

  return {
    formData,
    setFormData,
    isReadOnly,
    handleChange,
    handleIndustryChange,
    industryOptions,
    categoryOptions,
    countryOptions,
    renderField,
  };
}
